import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'How does the GPS tracking work?',
    answer: 'TripData uses your phone\'s GPS to automatically track your location while you drive. Simply tap "Start Trip" before you leave, and the app records your route in the background. It works even when your screen is off.',
  },
  {
    question: 'Is my data private and secure?',
    answer: 'Yes. Your trip data is stored locally on your device by default. If you upgrade to Pro, your data is encrypted and synced securely to the cloud. We never sell or share your personal information.',
  },
  {
    question: 'What\'s the difference between Free and Pro?',
    answer: 'Free gives you up to 30 trips per month with 1 vehicle, business/private categories, and Excel export — all stored locally. Pro unlocks unlimited trips, unlimited vehicles, cloud sync, saved locations, analytics dashboard, Google addresses.',
  },
  {
    question: 'Does it drain my battery?',
    answer: 'TripData is optimized for minimal battery usage. GPS tracking only runs when you\'re actively recording a trip, and we use efficient location sampling to balance accuracy with battery life.',
  },
  {
    question: 'How do I export my trips?',
    answer: 'Tap the export button in the app to download your trips as an Excel spreadsheet. You can filter by date range and category before exporting. Great for expense reports or tax records.',
  },
  {
    question: 'Can I track multiple vehicles?',
    answer: 'Yes, with Pro. You can add unlimited vehicles and switch between them. Each vehicle tracks its own odometer and trip history separately.',
  },
  {
    question: 'How much does Pro cost?',
    answer: 'Pro is R495/year. The free tier gives you 30 trips per month forever, so you can try the app properly before deciding to upgrade.',
  },
  {
    question: 'What are TripData vouchers?',
    answer: 'Businesses and fleet operators can purchase voucher codes to give their drivers free Pro access. It\'s a great value-add for companies that want to equip their team without managing individual subscriptions. Contact us to find out more.',
  },
  {
    question: 'When will the app be available?',
    answer: 'TripData launches in April 2026 on both iOS and Android. Sign up for notifications to be the first to know when it\'s live.',
  },
  {
    question: 'Can I cancel my Pro subscription?',
    answer: 'Yes, anytime. Cancel through your App Store or Google Play subscription settings. You\'ll keep Pro features until the end of your billing period.',
  },
];

const FAQ = () => {
  const theme = useTheme();
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section
      id="faq"
      className="py-32"
      style={{ backgroundColor: theme.card }}
    >
      <div className="container-app">

        {/* Header */}
        <div className="text-center mb-16">
          <h2
            className="text-responsive-lg font-bold tracking-tight mb-6"
            style={{ color: theme.text }}
          >
            Questions?{' '}
            <span className="gradient-text">Answers.</span>
          </h2>
          <p
            className="text-xl max-w-xl mx-auto"
            style={{ color: theme.textSecondary }}
          >
            Everything you need to know about TripData.
          </p>
        </div>

        {/* FAQ list */}
        <div className="max-w-2xl mx-auto">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border-b last:border-b-0"
              style={{ borderColor: theme.border }}
            >
              <button
                onClick={() => toggleFaq(index)}
                className="w-full py-6 flex items-center justify-between text-left"
              >
                <span
                  className="text-lg font-medium pr-8"
                  style={{ color: theme.text }}
                >
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                  style={{ color: theme.textMuted }}
                />
              </button>

              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 pb-6' : 'max-h-0'
                }`}
              >
                <p
                  className="text-base leading-relaxed"
                  style={{ color: theme.textSecondary }}
                >
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default FAQ;